import { useEffect, useRef, useState } from "react";

/**
 * Per-panel smoke FX. Ported from `_legacy/xaes-fx.js` `initSmokeFX`.
 * A grid of dots is overlaid on the canvas; each dot's brightness
 * follows a multi-octave sine "smoke field" with a slow upward drift.
 *
 * Theme-aware colour: pass `cssVarName` (e.g. `"--accent-sky"`) and the
 * canvas resolves the live hex value from
 * `getComputedStyle(documentElement)` each time the `<html>` class
 * attribute changes — that's what next-themes toggles when the user
 * flips light/dark. The `color` prop stays as a fallback for callers
 * that need an explicit RGB triple (e.g. WS8's melon brand mix).
 *
 * StrictMode-safe by construction (see PlasmaCanvas for the pattern
 * notes — same idea: `rafId` ref + `disposed` flag for idempotent
 * teardown).
 */

const SPACING = 14;
const DOT_RADIUS = 1.2;
const DECAY = 0.97;

function smokeField(nx: number, ny: number, t: number): number {
  const drift = t * 0.12;
  let v = 0;
  v += 1.0 * Math.sin(nx * 2.3 + (ny - drift) * 2.1 + t * 0.33);
  v += 0.7 * Math.sin(nx * 5.1 + (ny - drift) * 4.7 + t * 0.51 + 1.3);
  v += 0.5 * Math.sin(nx * 3.7 + (ny - drift) * 8.2 + t * 0.39 + 2.6);
  v += 0.35 * Math.sin(nx * 9.4 + (ny - drift) * 3.3 + t * 0.63 + 0.8);
  v += 0.25 * Math.sin(nx * 7.2 + (ny - drift) * 12.1 + t * 0.27 + 3.7);
  v += 0.18 * Math.sin(nx * 14.8 + (ny - drift) * 6.5 + t * 0.81 + 1.9);
  v += 0.12 * Math.sin(nx * 1.5 + (ny - drift) * 18.3 + t * 0.21 + 4.2);
  const total = 1.0 + 0.7 + 0.5 + 0.35 + 0.25 + 0.18 + 0.12;
  const norm = (v + total) / (2 * total);
  return Math.max(0, (norm - 0.52) / (1 - 0.52));
}

/**
 * Parse a `#rrggbb` or `#rgb` hex string into a `[r, g, b]` triple.
 * Returns `null` for unparseable input so the caller can fall back.
 */
function parseHex(input: string): [number, number, number] | null {
  const trimmed = input.trim();
  const m6 = trimmed.match(/^#([0-9a-fA-F]{6})$/);
  if (m6) {
    const hex = m6[1]!;
    return [
      parseInt(hex.slice(0, 2), 16),
      parseInt(hex.slice(2, 4), 16),
      parseInt(hex.slice(4, 6), 16),
    ];
  }
  const m3 = trimmed.match(/^#([0-9a-fA-F]{3})$/);
  if (m3) {
    const hex = m3[1]!;
    const r = parseInt(hex[0]! + hex[0]!, 16);
    const g = parseInt(hex[1]! + hex[1]!, 16);
    const b = parseInt(hex[2]! + hex[2]!, 16);
    return [r, g, b];
  }
  return null;
}

type SmokeCanvasProps = {
  className?: string;
  /**
   * RGB triple for the glow colour. Used when `cssVarName` is omitted.
   * Defaults to DXC True Blue (#4995FF).
   */
  color?: [number, number, number];
  /**
   * CSS custom property name (e.g. `"--accent-sky"`) to resolve at
   * runtime. Overrides `color` when set. Re-resolves on `<html>`
   * `class` attribute changes so the smoke theme-swaps with the UI.
   */
  cssVarName?: string;
  /** Accessible label for screen readers. */
  ariaLabel?: string;
};

const DEFAULT_COLOR: [number, number, number] = [73, 149, 255];

function readVar(cssVarName: string): [number, number, number] | null {
  if (typeof window === "undefined") return null;
  const raw = getComputedStyle(document.documentElement).getPropertyValue(
    cssVarName,
  );
  if (!raw) return null;
  return parseHex(raw);
}

export function SmokeCanvas({
  className,
  color,
  cssVarName,
  ariaLabel = "Smoke field animation",
}: SmokeCanvasProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  // Live resolved colour. When `cssVarName` is set we track theme
  // changes via MutationObserver; otherwise the prop wins and the
  // effect below stays in sync via dependency array.
  const [resolvedColor, setResolvedColor] = useState<
    [number, number, number]
  >(() => {
    if (cssVarName) {
      const fromVar = readVar(cssVarName);
      if (fromVar) return fromVar;
    }
    return color ?? DEFAULT_COLOR;
  });

  useEffect(() => {
    if (!cssVarName) {
      setResolvedColor(color ?? DEFAULT_COLOR);
      return;
    }

    function reresolve() {
      if (!cssVarName) return;
      const next = readVar(cssVarName);
      if (next) setResolvedColor(next);
    }
    // Always read once on mount / prop change in case the theme was
    // toggled before the observer attached.
    reresolve();

    const observer = new MutationObserver(reresolve);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });
    return () => observer.disconnect();
  }, [cssVarName, color]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    const t0 = performance.now();
    const [r, g, b] = resolvedColor;

    type Dot = { x: number; y: number; glow: number };
    let dots: Dot[] = [];
    let w = 0;
    let h = 0;

    function resize() {
      if (!canvas || !ctx) return;
      w = canvas.clientWidth;
      h = canvas.clientHeight;
      if (!w || !h) return;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      dots = [];
      const cols = Math.ceil(w / SPACING) + 2;
      const rows = Math.ceil(h / SPACING) + 2;
      for (let row = 0; row < rows; row++) {
        for (let col = 0; col < cols; col++) {
          dots.push({ x: col * SPACING, y: row * SPACING, glow: 0 });
        }
      }
    }

    let rafId: number | null = null;
    let disposed = false;

    function draw() {
      if (disposed || !ctx) return;
      if (!w || !h) {
        // Canvas not laid out yet — try again next frame
        rafId = requestAnimationFrame(draw);
        return;
      }
      const t = (performance.now() - t0) * 0.001;
      ctx.clearRect(0, 0, w, h);
      for (let i = 0; i < dots.length; i++) {
        const d = dots[i]!;
        const hit = smokeField(d.x / w, d.y / h, t);
        d.glow = Math.max(d.glow * DECAY, hit);
        if (d.glow < 0.006) continue;
        const a = d.glow * 0.35;
        ctx.beginPath();
        ctx.arc(d.x, d.y, DOT_RADIUS, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${r},${g},${b},${a.toFixed(3)})`;
        ctx.fill();
      }
      rafId = requestAnimationFrame(draw);
    }

    // Some parent layouts can take a tick before the canvas has
    // measurable dimensions. Re-resize via ResizeObserver to catch
    // late-arriving size and orientation changes.
    const ro = new ResizeObserver(resize);
    ro.observe(canvas);

    resize();
    window.addEventListener("resize", resize);
    rafId = requestAnimationFrame(draw);

    return () => {
      disposed = true;
      if (rafId !== null) cancelAnimationFrame(rafId);
      ro.disconnect();
      window.removeEventListener("resize", resize);
    };
  }, [resolvedColor]);

  return (
    <canvas
      ref={canvasRef}
      className={className}
      role="img"
      aria-label={ariaLabel}
    />
  );
}
